import { StrictMode, Suspense, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import {
	BrowserRouter,
	Navigate,
	Outlet,
	Route,
	Routes,
	useParams,
} from "react-router-dom";
import { ConnectedAccountHub } from "@/components/account/ConnectedAccountHub";
import { AuthEntry } from "@/components/auth/AuthEntry";
import { BillingMembershipPage } from "@/components/billing/BillingMembershipPage";
import { ProjectCanvasWorkspace } from "@/components/canvas/ProjectCanvasWorkspace";
import { ProjectInviteLanding } from "@/components/canvas/ProjectInviteLanding";
import { PublicProjectShareView } from "@/components/canvas/PublicProjectShareView";
import { CommunityThread } from "@/components/community/CommunityThread";
import { DiscoveryHub } from "@/components/discovery/DiscoveryHub";
import { InspirationDetail } from "@/components/discovery/InspirationDetail";
import { PlaylistDetail } from "@/components/discovery/PlaylistDetail";
import { SearchExperience } from "@/components/discovery/SearchExperience";
import { PersistentFeedback } from "@/components/feedback/PersistentFeedback";
import { CircleDanceTutorialImagine } from "@/components/tutorial/CircleDanceTutorialImagine";
import { GenArtView } from "@/components/genart/GenArtView";
import { LibraryExperience } from "@/components/library/LibraryExperience";
import { MakeStudio } from "@/components/make/MakeStudio";
import { TrackEditView } from "@/components/make/TrackEditView";
import { MessageThread } from "@/components/messages/MessageThread";
import { MessagesInbox } from "@/components/messages/MessagesInbox";
import { GenerationPlayer } from "@/components/player/GenerationPlayer";
import { PlaybackRecent } from "@/components/player/PlaybackRecent";
import { ProfileEditHub } from "@/components/profile/EditProfileView";
import { PromotionDashboard } from "@/components/promotion/PromotionDashboard";
import { ShazamPage } from "@/components/recognition/ShazamPage";
import {
	AudioQualitySettingsDetail,
	BrowserPermissionsSettingsDetail,
	DevicePlaybackSettingsDetail,
	PlayerSettingsDetail,
} from "@/components/settings/SettingsDetailPages";
import { SettingsHub } from "@/components/settings/SettingsHub";
import { ArtistShell } from "@/components/shell/ArtistShell";
import { ArtistRuntimeConfigurationProvider } from "@/components/runtime/ArtistRuntimeConfigurationProvider";
import { NoteFeed } from "@/components/social/NoteFeed";
import { NoteComposeHub } from "@/components/social/SocialHubs";
import { browserArtistRuntimeConfiguration } from "@/domain/auth/AuthProviderConfiguration";
import { ArtistI18nProvider, useTranslation } from "@/i18n/ArtistI18n";
import {
	fetchPublicProjectShare,
	type PublicProjectShare,
	PublicProjectShareNotFoundError,
} from "@/domain/projectCanvas/PublicProjectShare";
import "@/styles/globals.css";
import "@/styles/artist-ds.css";

function ShellRoute() {
	return (
		<ArtistShell>
			<Outlet />
		</ArtistShell>
	);
}

const artistRuntimeConfiguration = browserArtistRuntimeConfiguration();
const builtBaseName = import.meta.env.BASE_URL.replace(/\/$/u, "") || "/";
const routerBaseName = window.location.pathname.startsWith(`${builtBaseName}/`)
	? builtBaseName
	: "/";

function RouteId({
	component: Component,
}: Readonly<{ component: (props: { id: string }) => React.JSX.Element }>) {
	const { id } = useParams<{ id: string }>();
	return id ? <Component id={id} /> : <Navigate to="/discover" replace />;
}

function PublicTrackRoute() {
	const { id } = useParams<{ id: string }>();
	return id ? <GenerationPlayer publicTrackId={id} /> : <Navigate to="/discover" replace />;
}

function InviteRoute() {
	const { inviteId } = useParams<{ inviteId: string }>();
	return inviteId ? (
		<ProjectInviteLanding inviteId={inviteId} />
	) : (
		<Navigate to="/discover" replace />
	);
}

function PublicShareRoute() {
	const { t } = useTranslation();
	const { slug } = useParams<{ slug: string }>();
	const [share, setShare] = useState<PublicProjectShare | null>(null);
	const [state, setState] = useState<"loading" | "ready" | "missing" | "error">(
		"loading",
	);

	useEffect(() => {
		let active = true;
		setState("loading");
		setShare(null);
		if (!slug) {
			setState("missing");
			return;
		}
		void fetchPublicProjectShare(slug)
			.then((value) => {
				if (!active) return;
				setShare(value);
				setState("ready");
				document.title = `${value.document.project.title.value} · Artist`;
			})
			.catch((error: unknown) => {
				if (!active) return;
				setState(
					error instanceof PublicProjectShareNotFoundError ? "missing" : "error",
				);
			});
		return () => {
			active = false;
		};
	}, [slug]);

	if (state === "loading") {
		return <main className="status-page"><p role="status">{t("share.loading")}</p></main>;
	}
	if (state === "missing") {
		return <main className="status-page"><h1>{t("share.missing.title")}</h1><p>{t("share.missing.message")}</p></main>;
	}
	if (state === "error" || share === null || !slug) {
		return <main className="status-page"><h1>{t("share.error.title")}</h1><p>{t("share.error.message")}</p></main>;
	}
	return <PublicProjectShareView share={share} slug={slug} />;
}

function LegalPage({ kind }: Readonly<{ kind: "privacy" | "terms" }>) {
	const { t } = useTranslation();
	return kind === "privacy" ? (
		<article className="legal-page">
			<p className="eyebrow">{t("legal.privacy.eyebrow")}</p>
			<h1>{t("legal.privacy.title")}</h1>
			<p>{t("legal.privacy.body1")}</p>
			<p>{t("legal.privacy.body2")}</p>
		</article>
	) : (
		<article className="legal-page">
			<p className="eyebrow">{t("legal.terms.eyebrow")}</p>
			<h1>{t("legal.terms.title")}</h1>
			<p>{t("legal.terms.body1")}</p>
			<p>{t("legal.terms.body2")}</p>
		</article>
	);
}

function AppRoutes() {
	return (
		<>
			<Routes>
				<Route path="/" element={<Navigate to="/discover" replace />} />
				<Route path="/i/:inviteId" element={<InviteRoute />} />
				<Route path="/s/:slug" element={<PublicShareRoute />} />
				<Route element={<ShellRoute />}>
					<Route path="/discover" element={<DiscoveryHub />} />
					<Route path="/auth" element={<AuthEntry />} />
					<Route path="/account" element={<ConnectedAccountHub />} />
					<Route path="/account/connected" element={<ConnectedAccountHub />} />
					<Route path="/billing/membership" element={<BillingMembershipPage />} />
					<Route path="/canvas" element={<ProjectCanvasWorkspace />} />
					<Route path="/community" element={<NoteFeed />} />
					<Route path="/community/thread/:id" element={<RouteId component={CommunityThread} />} />
					<Route path="/genart" element={<GenArtView />} />
					<Route path="/inspirations/:id" element={<RouteId component={InspirationDetail} />} />
					<Route path="/library" element={<Suspense fallback={<section className="status-page"><p role="status">正在打开资料库…</p></section>}><LibraryExperience /></Suspense>} />
					<Route path="/make" element={<MakeStudio />} />
					<Route path="/messages/inbox" element={<MessagesInbox />} />
					<Route path="/messages/thread/:id" element={<RouteId component={MessageThread} />} />
					<Route path="/note/compose" element={<NoteComposeHub />} />
					<Route path="/player" element={<GenerationPlayer />} />
					<Route path="/track/:id" element={<PublicTrackRoute />} />
					<Route path="/playlist/:id" element={<RouteId component={PlaylistDetail} />} />
					<Route path="/privacy" element={<LegalPage kind="privacy" />} />
					<Route path="/profile/edit" element={<ProfileEditHub />} />
					<Route path="/promotion" element={<PromotionDashboard />} />
					<Route path="/recent" element={<PlaybackRecent />} />
					<Route path="/recognition" element={<ShazamPage />} />
					<Route path="/recognition/shazam" element={<ShazamPage />} />
					<Route path="/search" element={<SearchExperience />} />
					<Route path="/settings" element={<SettingsHub />} />
					<Route path="/settings/audio-quality" element={<AudioQualitySettingsDetail />} />
					<Route path="/settings/device-playback" element={<DevicePlaybackSettingsDetail />} />
					<Route path="/settings/permissions" element={<BrowserPermissionsSettingsDetail />} />
					<Route path="/settings/player" element={<PlayerSettingsDetail />} />
					<Route path="/terms" element={<LegalPage kind="terms" />} />
					<Route path="/track/edit" element={<TrackEditView />} />
					<Route path="/tutorial/circle-dance" element={<CircleDanceTutorialImagine />} />
				</Route>
				<Route path="*" element={<Navigate to="/discover" replace />} />
			</Routes>
			<PersistentFeedback />
		</>
	);
}

createRoot(document.getElementById("root")!).render(
	<StrictMode>
		<ArtistRuntimeConfigurationProvider value={artistRuntimeConfiguration}>
			<ArtistI18nProvider defaultLocale={artistRuntimeConfiguration.region === "china" ? "zh-Hans" : "system"}>
				<BrowserRouter basename={routerBaseName}>
					<AppRoutes />
				</BrowserRouter>
			</ArtistI18nProvider>
		</ArtistRuntimeConfigurationProvider>
	</StrictMode>,
);
